<template>
  <div class="whatsapp-sharing d-inline-flex">
    <v-dialog
      v-model="dialog"
      :width="isMobile ? undefined : 1050"
      :height="isMobile ? undefined : '90vh'"
      :fullscreen="isMobile"
      content-class="whatsapp-sharing-dialog"
      persistent
    >
      <template #activator="{ props: dialogProps }">
        <v-tooltip
          location="top"
          :disabled="!!props.image"
        >
          <template #activator="{ props: tooltipProps }">
            <div v-bind="tooltipProps">
              <v-btn
                v-bind="dialogProps"
                color="rain-forest"
                class="ml-md-2 my-2"
                :disabled="props.disabled || !props.image"
                data-product-fruits="content-creator-share-whatsapp"
                @click="handleOpen"
              >
                <v-icon
                  class="mr-1"
                  size="18"
                >
                  mdi-whatsapp
                </v-icon>
                {{ t('content-creator.whatsapp.button') }}
              </v-btn>
            </div>
          </template>
          <span>{{ t('content-creator.whatsapp.no-image-tooltip') }}</span>
        </v-tooltip>
      </template>

      <v-card class="d-flex flex-column h-100">
        <v-card-title class="pa-0 flex-grow-0">
          <v-toolbar
            flat
            color="rain-forest"
          >
            <v-toolbar-title class="text-white">
              {{
                isMobile
                  ? t('content-creator.whatsapp.desktop.preview-title')
                  : t('content-creator.whatsapp.dialog-title')
              }}
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn
                icon
                @click="dialog = false"
              >
                <v-icon
                  size="large"
                  class="text-white"
                >
                  mdi-close
                </v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>

        <template v-if="isMobile">
          <WhatsAppStatusPreview
            :media="props.image"
            :text="props.text"
            :media-type="props.mediaType"
            fill-height
          />
          <div class="mobile-actions">
            <v-btn
              variant="text"
              @click="dialog = false"
            >
              {{ t('labels.back') }}
            </v-btn>
            <v-tooltip
              location="top"
              :disabled="isWebShareSupported"
            >
              <template #activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps">
                  <v-btn
                    color="rain-forest"
                    :loading="isSharing"
                    :disabled="!isWebShareSupported"
                    @click="share"
                  >
                    {{ t('content-creator.whatsapp.mobile.share-button') }}
                  </v-btn>
                </div>
              </template>
              <span>{{ t('content-creator.whatsapp.mobile.web-share-not-supported') }}</span>
            </v-tooltip>
          </div>
        </template>

        <template v-else>
          <div class="desktop-body d-flex flex-grow-1 overflow-hidden">
            <div class="desktop-left-col pa-6 d-flex flex-column">
              <h2 class="wa-header mb-5">{{ t('content-creator.whatsapp.guide-title') }}</h2>
              <p class="wa-step-bold mb-2">{{ t('content-creator.whatsapp.desktop.step1') }}</p>
              <p class="wa-step-text mb-5">{{ t('content-creator.whatsapp.desktop.step2') }}</p>

              <div class="d-flex justify-center mb-4">
                <div
                  v-if="qrCodeDataUrl"
                  class="qr-container pa-3"
                >
                  <img
                    :src="qrCodeDataUrl"
                    :alt="t('content-creator.whatsapp.qr-code-alt')"
                    width="360"
                    height="360"
                  />
                </div>
                <v-progress-circular
                  v-else
                  indeterminate
                  color="rain-forest"
                />
              </div>

              <div class="desktop-fallback mt-auto pt-4 d-flex flex-column align-center">
                <p class="wa-step-hint text-center mb-5">
                  {{ t('content-creator.whatsapp.desktop.step3') }}
                </p>

                <v-btn
                  variant="outlined"
                  color="success"
                  class="mb-4"
                  :loading="isDownloading"
                  @click="downloadMedia"
                >
                  <v-icon
                    class="mr-1"
                    size="18"
                  >
                    mdi-download
                  </v-icon>
                  {{ t('content-creator.whatsapp.download-button') }}
                </v-btn>
              </div>
            </div>

            <div class="desktop-right-col d-flex flex-column">
              <h2 class="wa-header pa-6 pb-4">
                {{ t('content-creator.whatsapp.desktop.preview-title') }}
              </h2>
              <WhatsAppStatusPreview
                :media="props.image"
                :text="props.text"
                :media-type="props.mediaType"
                fill-height
              />
            </div>
          </div>

          <div class="desktop-actions pa-4 d-flex justify-space-between">
            <v-btn
              variant="text"
              @click="dialog = false"
            >
              {{ t('labels.back') }}
            </v-btn>
            <v-btn
              color="rain-forest"
              @click="dialog = false"
            >
              {{ t('buttons.finish') }}
            </v-btn>
          </div>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable'
import { useQRCode } from '@vueuse/integrations/useQRCode'
import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import { AnimationEvents } from '@/lib/animation'
import bus, { eventNames } from '@/lib/eventBus'
import { createAndDownloadZip } from '@/lib/generateZip'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import type { TrackingPlugin } from '@/plugins/tracking'

import SAVE_CONTENT from '../queries/saveContent.gql'
import { buildWhatsAppShareUrl, useWhatsAppShare } from './useWhatsAppShare'
import WhatsAppStatusPreview from './WhatsAppStatusPreview.vue'

const tracking = inject<TrackingPlugin>('tracking')

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    image?: string
    text?: string
    topic?: string
    topicId?: string
    textKind?: string
    imageSource?: string
    isNotLicensedForDownload?: boolean
    mediaType?: string
  }>(),
  {
    disabled: false,
    image: '',
    text: '',
    topic: '',
    topicId: '',
    textKind: '',
    imageSource: '',
    isNotLicensedForDownload: false,
    mediaType: '',
  }
)

const emit = defineEmits<{ contentSaved: [] }>()

const { t } = useI18n()
const { smAndDown } = useDisplay()
const { mutate: saveContentMutation } = useMutation(SAVE_CONTENT)

const isMobile = computed(() => smAndDown.value)

const isWebShareSupported = computed(() => !!navigator?.share)

const qrUrl = ref('')
const qrCodeDataUrl = useQRCode(qrUrl, { width: 360, margin: 1 })

const { isSharing, shareNative } = useWhatsAppShare()

const dialog = ref(false)
const isDownloading = ref(false)
async function handleOpen() {
  if (!isMobile.value && props.image) {
    qrUrl.value = `${window.location.origin}${buildWhatsAppShareUrl(props.text, props.image, props.mediaType)}`
    // Only save for desktop/QR path; mobile saves on share()
    await saveContent()
  }
}

async function saveContent(): Promise<void> {
  try {
    await saveContentMutation({
      input: {
        text: props.text ?? null,
        imagePath: props.image ?? null,
        imageSource: props.imageSource,
        textKind: props.textKind,
        topicId: props.topicId,
      },
    })
    bus.$emit(eventNames.TRIGGER_ANIMATION, AnimationEvents.CONTENT_DOWNLOADED)
    emit('contentSaved')
  } catch {
    // Non-blocking
  }
}

async function share(): Promise<void> {
  try {
    await saveContent()
    const didShare = await shareNative(props.text, props.image, props.mediaType)
    if (!didShare) {
      // Share was cancelled, or no share target was available — not an error, nothing to report
      return
    }
    tracking?.heapEvent('post_published', { channel: 'WHATSAPP_STATUS' })
    showSnackbarMessage('success', t('content-creator.whatsapp.mobile.toast-image-ready'))
    showSnackbarMessage('success', t('content-creator.whatsapp.mobile.toast-text-copied'))
    showSnackbarMessage('info', t('content-creator.whatsapp.desktop.saved-to-history'))
  } catch (err: unknown) {
    if (err instanceof Error) {
      if (err.name === 'NotSupportedError') {
        showSnackbarMessage('error', t('content-creator.whatsapp.mobile.web-share-not-supported'))
      } else {
        showSnackbarMessage('error', t('alerts.content-creator.general.error'))
      }
    }
  }
}

async function downloadMedia(): Promise<void> {
  isDownloading.value = true
  try {
    await createAndDownloadZip({
      text: props.text ?? null,
      imagePath: props.image ?? null,
      carouselImages: [],
      textKind: t('content-creator.text-kind-types.whatsapp-status'),
      topic: props.topic || null,
      url: null,
    })
  } catch {
    showSnackbarMessage('error', t('alerts.content-creator.create-zip-file.error'))
  } finally {
    isDownloading.value = false
  }
}
</script>

<style scoped>
.wa-header {
  font-weight: 500;
  font-size: 1.375rem;
  line-height: 28px;
  margin: 0;
  color: rgb(var(--v-theme-rain-forest));
}

.wa-step-bold {
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  margin: 0;
  color: rgb(var(--v-theme-rain-forest));
}

.wa-step-text {
  font-size: 16px;
  line-height: 22px;
  margin: 0;
}

.wa-step-hint {
  font-style: italic;
  font-size: 14px;
  line-height: 18px;
  color: rgb(var(--v-theme-grey));
  margin: 0;
}

.desktop-body {
  min-height: 0;
}

.desktop-left-col {
  flex: 1;
  overflow-y: auto;
}

.desktop-right-col {
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.qr-container {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  display: inline-flex;
}

.mobile-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  margin-bottom: 60px;
  flex-shrink: 0;
}

.desktop-actions {
  flex-shrink: 0;
}
</style>
